import type { Area } from "../types.js";

/**
 * Single place to tune furniture progression and the gold shop.
 * `slot` is a named spot in the apartment (see packages/sprites room layout).
 */
export interface MilestoneItem {
  id: string;
  name: string;
  area: Area;
  level: number;
  slot: string;
  /** Replaces another item visually (bed replaces mattress etc.). */
  replaces?: string;
}

export interface ShopItem {
  id: string;
  name: string;
  price: number;
  slot: string;
}

export const MILESTONES: readonly MilestoneItem[] = [
  { id: "mat", name: "mata do ćwiczeń", area: "body", level: 2, slot: "floor_left" },
  { id: "pullup_bar", name: "drążek w drzwiach", area: "body", level: 3, slot: "door_frame" },
  { id: "dumbbells", name: "hantle", area: "body", level: 5, slot: "floor_left_small" },
  { id: "bench", name: "ławka treningowa", area: "body", level: 7, slot: "floor_left_back" },

  { id: "book_stack", name: "stos książek", area: "mind", level: 2, slot: "corner_books" },
  { id: "bookshelf", name: "regał", area: "mind", level: 3, slot: "wall_shelf", replaces: "book_stack" },
  { id: "reading_lamp", name: "lampka do czytania", area: "mind", level: 5, slot: "lamp" },
  { id: "armchair", name: "fotel", area: "mind", level: 7, slot: "armchair" },

  { id: "laptop", name: "laptop", area: "work", level: 2, slot: "desk_area" },
  { id: "desk", name: "biurko", area: "work", level: 3, slot: "desk_area" },
  { id: "chair", name: "krzesło", area: "work", level: 5, slot: "desk_chair" },
  { id: "monitor2", name: "drugi monitor", area: "work", level: 7, slot: "desk_top" },

  { id: "photos", name: "zdjęcia z bliskimi", area: "social", level: 2, slot: "wall_photos" },
  { id: "board_games", name: "półka z planszówkami", area: "social", level: 4, slot: "wall_games" },

  // Home has no visible level: these follow the hidden care points of everyday chores.
  { id: "trash_bin", name: "kosz na śmieci", area: "home", level: 2, slot: "by_fridge" },
  { id: "plant", name: "roślina", area: "home", level: 3, slot: "window_sill" },
  { id: "rug", name: "dywan", area: "home", level: 4, slot: "floor_center" },
  { id: "curtains", name: "zasłony", area: "home", level: 6, slot: "window" },
  { id: "bed", name: "łóżko", area: "home", level: 8, slot: "bed", replaces: "mattress" },
];

export const SHOP: readonly ShopItem[] = [
  { id: "poster", name: "plakat", price: 120, slot: "wall_poster" },
  { id: "radio", name: "radio", price: 200, slot: "shelf_small" },
  { id: "vacuum", name: "odkurzacz", price: 250, slot: "by_door" },
  { id: "guitar", name: "gitara", price: 350, slot: "wall_guitar" },
  { id: "dartboard", name: "tarcza do rzutek", price: 300, slot: "wall_darts" },
  { id: "console", name: "konsola", price: 600, slot: "floor_center_front" },
  { id: "tv", name: "telewizor", price: 900, slot: "wall_tv" },
];

/** Gold bonus for real-world money events. Amounts in PLN are never stored. */
export const MONEY_BONUS = { small: 40, medium: 120, large: 300 } as const;

/** A large money event gifts the first of these the avatar doesn't own yet. */
export const MONEY_GIFTS: readonly string[] = ["console", "tv"];

export function itemName(id: string): string {
  return MILESTONES.find((m) => m.id === id)?.name ?? SHOP.find((s) => s.id === id)?.name ?? id;
}

export function shopItem(id: string): ShopItem | undefined {
  return SHOP.find((s) => s.id === id);
}
